import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { questionnaireSteps } from "../data/mock";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuestionnairePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [direction, setDirection] = useState("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const step = questionnaireSteps[currentStep];
  const progress = ((currentStep + 1) / questionnaireSteps.length) * 100;

  const handleSelect = (value) => {
    if (step.type === "multi-choice") {
      const current = answers[step.id] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setAnswers({ ...answers, [step.id]: updated });
    } else if (step.type === "image-choice" || step.type === "single-choice" || step.type === "palette-choice") {
      setAnswers({ ...answers, [step.id]: value });
    }
  };

  const handleTextChange = (value) => {
    setAnswers({ ...answers, [step.id]: value });
  };

  const handleContactChange = (field, value) => {
    const current = answers[step.id] || {};
    setAnswers({ ...answers, [step.id]: { ...current, [field]: value } });
  };

  const canProceed = () => {
    const answer = answers[step.id];
    if (step.type === "textarea") return true;
    if (step.type === "contact") {
      const contact = answer || {};
      return contact.firstName && contact.email;
    }
    if (step.type === "multi-choice") return answer && answer.length > 0;
    return !!answer;
  };

  const goNext = async () => {
    if (currentStep < questionnaireSteps.length - 1) {
      setDirection("next");
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsTransitioning(false);
      }, 300);
      return;
    }
    // Final step → submit
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire`, { answers });
      setIsComplete(true);
      toast.success("Your journey has begun! We'll be in touch soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't submit your responses. Please try again or email us directly."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setDirection("back");
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  if (isComplete) {
    return (
      <main className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 rounded-full bg-[#B8977E]/20 flex items-center justify-center mx-auto mb-8">
            <Check size={28} className="text-[#B8977E]" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
            Your journey begins here.
          </h1>
          <p className="font-body text-stone-500 text-base leading-relaxed mb-8">
            Thank you for sharing your vision with us. Our design team will review
            your responses and reach out within 48 hours to schedule your
            complimentary consultation.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-stone-900 group"
          >
            Return Home
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAFAF7] flex flex-col">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-stone-200">
        <div
          className="h-full bg-[#B8977E] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="pt-8 px-6 md:px-12 flex items-center justify-between max-w-[1400px] mx-auto w-full">
        <a href="/" className="font-display text-xl font-semibold text-stone-900 tracking-tight">
          Catapult
        </a>
        <span className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-400">
          Step {currentStep + 1} of {questionnaireSteps.length}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 md:px-12 py-12">
        <div
          className={`w-full max-w-3xl transition-all duration-300 ${
            isTransitioning
              ? direction === "next"
                ? "opacity-0 translate-x-8"
                : "opacity-0 -translate-x-8"
              : "opacity-100 translate-x-0"
          }`}
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-3 leading-[1.1]">
            {step.question}
          </h2>
          <p className="font-body text-stone-400 text-base md:text-lg mb-10">
            {step.subtitle}
          </p>

          {/* Image Choice */}
          {step.type === "image-choice" && (
            <div
              className={`grid gap-4 md:gap-5 ${
                step.options.some((o) => o.description)
                  ? "grid-cols-1 md:grid-cols-2"
                  : "grid-cols-2 md:grid-cols-4"
              }`}
            >
              {step.options.map((opt) => {
                const selected = answers[step.id] === opt.value;
                const hasDescription = Boolean(opt.description);
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`group text-left overflow-hidden bg-white transition-all duration-300 ${
                      selected
                        ? "ring-2 ring-[#B8977E] ring-offset-2"
                        : "hover:ring-1 hover:ring-stone-300 hover:ring-offset-1"
                    }`}
                    data-testid={`questionnaire-image-option-${opt.value}`}
                  >
                    <div className={`relative overflow-hidden ${hasDescription ? "aspect-[16/10]" : "aspect-[3/4]"}`}>
                      <img
                        src={opt.image}
                        alt={opt.label}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {!hasDescription && (
                        <span className="absolute bottom-4 left-4 font-body text-sm text-white">
                          {opt.label}
                        </span>
                      )}
                      {selected && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-[#B8977E] flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </div>
                    {hasDescription && (
                      <div className="p-6 md:p-7 border border-t-0 border-stone-200">
                        <h3 className="font-display text-2xl text-stone-900 mb-2">{opt.label}</h3>
                        <p className="font-body text-stone-500 text-sm leading-relaxed">
                          {opt.description}
                        </p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Palette Choice — editorial color-palette cards (no stock photos). */}
          {step.type === "palette-choice" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {step.options.map((opt) => {
                const selected = answers[step.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`group text-left bg-white border transition-all duration-500 ${
                      selected
                        ? "border-[#B8977E] ring-2 ring-[#B8977E]/30 ring-offset-2"
                        : "border-stone-200 hover:border-stone-400"
                    }`}
                    data-testid={`questionnaire-palette-option-${opt.value}`}
                  >
                    {/* Palette band */}
                    <div className="grid grid-cols-4 h-28 md:h-32">
                      {opt.palette.map((c, i) => (
                        <div
                          key={i}
                          style={{ backgroundColor: c }}
                          className="h-full transition-transform duration-500 group-hover:scale-y-[1.02]"
                        />
                      ))}
                    </div>
                    <div className="p-6 md:p-8 relative">
                      {selected && (
                        <div className="absolute top-6 right-6 w-6 h-6 bg-[#B8977E] flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                      <h3 className="font-display text-2xl md:text-3xl text-stone-900 mb-3 tracking-tight">
                        {opt.label}
                      </h3>
                      <p className="font-body text-stone-500 text-sm md:text-base leading-relaxed mb-5">
                        {opt.description}
                      </p>
                      <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#B8977E]">
                        {opt.accents}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {step.type === "single-choice" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {step.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`text-left px-6 py-5 border transition-all duration-300 ${
                    answers[step.id] === opt.value
                      ? "border-[#B8977E] bg-[#B8977E]/5"
                      : "border-stone-200 hover:border-stone-400 bg-white"
                  }`}
                  data-testid={`questionnaire-single-option-${opt.value}`}
                >
                  <span className="font-body text-base text-stone-800">
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Multi Choice */}
          {step.type === "multi-choice" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {step.options.map((opt) => {
                const isSelected = (answers[step.id] || []).includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`text-left px-6 py-5 border transition-all duration-300 flex items-center gap-4 ${
                      isSelected
                        ? "border-[#B8977E] bg-[#B8977E]/5"
                        : "border-stone-200 hover:border-stone-400 bg-white"
                    }`}
                    data-testid={`questionnaire-multi-option-${opt.value}`}
                  >
                    <div
                      className={`w-5 h-5 border flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
                        isSelected
                          ? "border-[#B8977E] bg-[#B8977E]"
                          : "border-stone-300"
                      }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                    <span className="font-body text-base text-stone-800">
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Textarea */}
          {step.type === "textarea" && (
            <textarea
              value={answers[step.id] || ""}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={step.placeholder}
              className="w-full h-48 px-6 py-5 border border-stone-200 bg-white font-body text-base text-stone-800 placeholder:text-stone-300 resize-none focus:outline-none focus:border-[#B8977E] transition-colors duration-300"
              data-testid="questionnaire-textarea"
            />
          )}

          {/* Contact */}
          {step.type === "contact" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {step.fields.map((field) => (
                <div key={field.name} className={field.name === "email" ? "md:col-span-2" : ""}>
                  <label className="font-body text-[11px] tracking-[0.15em] uppercase text-stone-400 mb-2 block">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    value={(answers[step.id] || {})[field.name] || ""}
                    onChange={(e) => handleContactChange(field.name, e.target.value)}
                    className="w-full px-5 py-4 border border-stone-200 bg-white font-body text-base text-stone-800 focus:outline-none focus:border-[#B8977E] transition-colors duration-300"
                    data-testid={`questionnaire-contact-${field.name}-input`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="px-6 md:px-12 pb-8 pt-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={goBack}
            disabled={currentStep === 0 || isSubmitting}
            className={`inline-flex items-center gap-2 font-body text-sm tracking-[0.08em] uppercase transition-colors duration-300 ${
              currentStep === 0 || isSubmitting
                ? "text-stone-300 cursor-not-allowed"
                : "text-stone-500 hover:text-stone-900"
            }`}
            data-testid="questionnaire-back-button"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <button
            onClick={goNext}
            disabled={!canProceed() || isSubmitting}
            className={`inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase px-8 py-3.5 transition-all duration-300 ${
              canProceed() && !isSubmitting
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : "bg-stone-200 text-stone-400 cursor-not-allowed"
            }`}
            data-testid="questionnaire-next-button"
          >
            {currentStep === questionnaireSteps.length - 1
              ? (isSubmitting ? "Submitting..." : "Submit")
              : "Continue"}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </main>
  );
}
